import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  X, 
  FileText, 
  Calendar, 
  Sparkles, 
  Plus,
  Search,
  Languages,
  Layers,
  Trash2,
  Send,
  PenTool,
  User,
  Globe,
  Clock
} from 'lucide-react';

// --- MOCK DATA ---
const SYSTEM_TEMPLATES = [
  { id: 'passport', en: { label: 'Passport', desc: 'High-res scan of biodata page.' }, fr: { label: 'Passeport', desc: 'Scan haute résolution de la page biométrique.' } },
  { id: 'photo', en: { label: 'Digital Photo', desc: '50mm x 70mm white background.' }, fr: { label: 'Photo numérique', desc: '50mm x 70mm fond blanc.' } },
  { id: 'bank', en: { label: 'Bank Statement', desc: 'Proof of funds (last 4 months).' }, fr: { label: 'Relevé bancaire', desc: 'Preuve de fonds (4 derniers mois).' } },
  { id: 'caq', en: { label: 'CAQ Approval', desc: 'Official CAQ letter from MIFI.' }, fr: { label: 'Lettre CAQ', desc: 'Lettre officielle du CAQ (MIFI).' } },
  { id: 'admission', en: { label: 'Letter of Acceptance', desc: 'Official LOA from the DLI.' }, fr: { label: 'Lettre d\'admission', desc: 'Lettre d\'acceptation officielle de l\'EED.' } },
  { id: 'medical', en: { label: 'Medical Exam', desc: 'eMedical information sheet.' }, fr: { label: 'Examen médical', desc: 'Fiche d\'information eMedical.' } },
];

const BUNDLES = [
  { 
    id: 'bundle_study', 
    label: 'Study Permit Starter', 
    items: ['passport', 'admission', 'bank', 'photo'] 
  },
  { 
    id: 'bundle_caq', 
    label: 'CAQ Application', 
    items: ['passport', 'admission'] 
  }
];

// --- THE MODAL COMPONENT ---
const RequestDocModal = ({ isOpen, onClose, onSubmit, clientDeadline, clientPreferredLanguage = 'en', consultantTemplates = [] }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [requestQueue, setRequestQueue] = useState([]);
  const [batchDeadline, setBatchDeadline] = useState('');
  
  // Tab state for the library
  const [activeTab, setActiveTab] = useState('system'); // 'system' or 'mine'
  
  // NEW: Drafting Language State (Defaults to client preference)
  const [draftingLang, setDraftingLang] = useState(clientPreferredLanguage);

  useEffect(() => {
    if (isOpen) {
      setRequestQueue([]); 
      setSearchTerm('');
      setActiveTab('system');
      setDraftingLang(clientPreferredLanguage); // Reset language to client default on open
      // Initialize batch deadline from prop
      setBatchDeadline(clientDeadline ? new Date(clientDeadline).toISOString().split('T')[0] : '');
    }
  }, [isOpen, clientDeadline, clientPreferredLanguage]);

  if (!isOpen) return null;

  // Helper to safely get localized string from backend (which might be String or Object)
  const getLoc = (data, lang) => {
    if (!data) return '';
    if (typeof data === 'string') return data;
    // Handle Mixed type { en: "...", fr: "..." }
    return data[lang] || data['en'] || Object.values(data)[0] || '';
  };

  const addToQueue = (template) => {
    // Use the currently selected drafting language
    const content = template[draftingLang] || template['en'];
    
    const newItem = {
      _tempId: Date.now() + Math.random(), 
      name: content.label,
      description: content.desc
    };
    setRequestQueue(prev => [...prev, newItem]);
  };

  const addCustomItem = () => {
    const newItem = {
      _tempId: Date.now() + Math.random(),
      name: '',
      description: '',
      isCustom: true 
    };
    setRequestQueue(prev => [newItem, ...prev]); 
  };

  const addBundleToQueue = (bundle) => {
    const newItems = bundle.items.map(itemId => {
      const template = SYSTEM_TEMPLATES.find(t => t.id === itemId);
      if (!template) return null;
      
      // Respect the drafting language for bundles too
      const content = template[draftingLang] || template['en'];
      
      return {
        _tempId: Date.now() + Math.random(),
        name: content.label,
        description: content.desc
      };
    }).filter(Boolean);
    setRequestQueue(prev => [...prev, ...newItems]);
  };

  // Add a Consultant Template (which is a bundle of docs)
  const addConsultantTemplateToQueue = (template) => {
    if (!template.documents || !Array.isArray(template.documents)) return;

    const newItems = template.documents.map(doc => {
      const name = getLoc(doc.name, draftingLang);
      const desc = getLoc(doc.description, draftingLang);
      
      return {
        _tempId: Date.now() + Math.random(),
        name: name,
        description: desc
      };
    });
    setRequestQueue(prev => [...prev, ...newItems]);
  };

  const removeFromQueue = (tempId) => {
    setRequestQueue(prev => prev.filter(item => item._tempId !== tempId));
  };

  const updateItem = (tempId, field, value) => {
    setRequestQueue(prev => prev.map(item => 
      item._tempId === tempId ? { ...item, [field]: value } : item
    ));
  };

  const handleSendAll = () => {
    // Apply the batch deadline to all items before sending
    const finalQueue = requestQueue.map(item => ({
      ...item,
      deadline: batchDeadline 
    }));
    
    onSubmit(finalQueue); 
    onClose();
  };

  // Filter Logic
  const getVisibleItems = () => {
    if (activeTab === 'system') {
      return SYSTEM_TEMPLATES.filter(t => {
        const searchLabel = t[draftingLang].label.toLowerCase(); 
        return !searchTerm || searchLabel.includes(searchTerm.toLowerCase());
      });
    } else {
      // Consultant Templates (Bundles)
      return consultantTemplates.filter(t => {
        const name = getLoc(t.name, draftingLang).toLowerCase();
        return !searchTerm || name.includes(searchTerm.toLowerCase());
      });
    }
  };

  const visibleItems = getVisibleItems();

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[650px] overflow-hidden flex flex-col border border-slate-100">
        
        {/* HEADER */}
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-gray-50/50">
          <div>
            <h3 className="text-lg font-bold text-slate-800">Request Documents</h3>
            <p className="text-xs text-slate-500">
              Compose your document request list for this client
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full text-slate-400 hover:text-slate-600 transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* SPLIT VIEW */}
        <div className="flex-1 flex overflow-hidden">
          
          {/* LEFT: LIBRARY (35%) */}
          <div className="w-[35%] bg-slate-50 border-r border-slate-200 flex flex-col">
            
            {/* Library Tabs */}
            <div className="flex border-b border-slate-200">
              <button 
                onClick={() => setActiveTab('system')}
                className={`flex-1 py-3 text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-2 transition-colors ${activeTab === 'system' ? 'bg-white text-blue-600 border-b-2 border-blue-600' : 'text-slate-500 hover:bg-slate-100'}`}
              >
                <Globe className="h-3 w-3" /> System
              </button>
              <button 
                onClick={() => setActiveTab('mine')}
                className={`flex-1 py-3 text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-2 transition-colors ${activeTab === 'mine' ? 'bg-white text-blue-600 border-b-2 border-blue-600' : 'text-slate-500 hover:bg-slate-100'}`}
              >
                <User className="h-3 w-3" /> My Templates
              </button>
            </div>

            {/* Search & Language Toggle */}
            <div className="p-4 border-b border-slate-200/50 space-y-3">
              <div className="relative flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-2 h-4 w-4 text-slate-400" />
                  <input 
                    type="text"
                    placeholder={activeTab === 'system' ? "Search library..." : "Search my templates..."}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  />
                </div>
                
                {/* LANGUAGE TOGGLE BUTTON */}
                <button 
                  onClick={() => setDraftingLang(prev => prev === 'en' ? 'fr' : 'en')}
                  className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-600 hover:text-blue-600 hover:border-blue-300 transition-colors flex items-center gap-1.5 shadow-sm"
                  title="Toggle Template Language"
                >
                  <Globe className="h-3 w-3" />
                  {draftingLang.toUpperCase()}
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-6 custom-scrollbar">
              
              {/* SYSTEM TAB VIEW */}
              {activeTab === 'system' && (
                <>
    

                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                        <Sparkles className="h-3 w-3" /> Templates ({draftingLang.toUpperCase()})
                      </h4>
                    </div>
                    
                    <div className="space-y-1">
                      {visibleItems.length > 0 ? visibleItems.map(t => (
                        <button
                          key={t.id}
                          onClick={() => addToQueue(t)}
                          className="w-full text-left px-3 py-2 rounded-md hover:bg-white hover:shadow-sm transition-all text-sm font-medium text-slate-600 hover:text-blue-600 flex justify-between items-center group"
                        >
                          {t[draftingLang].label}
                          <Plus className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </button>
                      )) : (
                        <div className="text-center py-4 text-xs text-slate-400 italic">
                           No system templates found.
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* MY TEMPLATES TAB VIEW */}
              {activeTab === 'mine' && (
                <div className="space-y-2">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                    <User className="h-3 w-3" /> Saved Templates ({visibleItems.length})
                  </h4>
                  
                  <div className="grid grid-cols-1 gap-2">
                    {visibleItems.length > 0 ? visibleItems.map(t => (
                      <button
                        key={t._id}
                        onClick={() => addConsultantTemplateToQueue(t)}
                        className="text-left px-3 py-3 rounded-lg bg-white border border-slate-200 shadow-sm hover:border-blue-400 hover:ring-1 hover:ring-blue-400 transition-all group"
                      >
                        <div className="flex justify-between items-start">
                           <div className="font-semibold text-sm text-slate-700 group-hover:text-blue-700 line-clamp-1">
                             {getLoc(t.name, draftingLang)}
                           </div>
                           <Plus className="h-3 w-3 text-slate-400 group-hover:text-blue-600" />
                        </div>
                        <div className="text-[10px] text-slate-400 mt-1">
                           Contains {t.documents ? t.documents.length : 0} documents
                        </div>
                      </button>
                    )) : (
                      <div className="text-center py-8 text-slate-400">
                         {searchTerm ? (
                            <p className="text-xs italic">No templates match your search.</p>
                         ) : (
                            <div className="space-y-2">
                               <p className="text-sm font-medium">No templates found.</p>
                               <p className="text-xs text-slate-400 px-4">Create templates in your Settings to see them here.</p>
                            </div>
                         )}
                      </div>
                    )}
                  </div>
                </div>
              )}

            </div>
          </div>

          {/* RIGHT: QUEUE / CART (65%) */}
          <div className="flex-1 flex flex-col bg-white">
            
            {/* Toolbar */}
            <div className="px-6 py-3 border-b border-slate-100 flex justify-between items-center bg-slate-50/30">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                Request List ({requestQueue.length})
              </span>
              <button 
                onClick={addCustomItem}
                className="text-xs font-bold text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-3 py-1.5 rounded-md transition-colors flex items-center gap-1"
              >
                <PenTool className="h-3 w-3" /> Write Custom
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar">
              {requestQueue.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-slate-400 space-y-3 opacity-60">
                  <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center">
                    <FileText className="h-8 w-8 text-slate-300" />
                  </div>
                  <p className="text-sm">Your request list is empty.</p>
                  <p className="text-xs">Select templates from the library or create a custom request.</p>
                  <button onClick={addCustomItem} className="mt-2 text-xs font-bold text-blue-600 hover:underline">
                    + Create Custom Request
                  </button>
                </div>
              ) : (
                requestQueue.map((item) => (
                  <div key={item._tempId} className={`group relative border rounded-xl p-4 transition-all hover:shadow-sm animate-in slide-in-from-bottom-2 duration-300 ${item.isCustom ? 'border-blue-200 bg-blue-50/30' : 'border-slate-200 hover:border-blue-300'}`}>
                    <button 
                      onClick={() => removeFromQueue(item._tempId)}
                      className="absolute top-2 right-2 p-1.5 text-slate-300 hover:text-red-500 rounded-md hover:bg-red-50 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>

                    <div className="grid gap-3">
                      <input 
                        className="font-semibold text-slate-800 border-none p-0 focus:ring-0 w-[90%] placeholder:text-slate-400 bg-transparent"
                        placeholder="Document Name (Required)"
                        value={item.name}
                        autoFocus={item.name === ''}
                        onChange={(e) => updateItem(item._tempId, 'name', e.target.value)}
                      />
                      <textarea 
                        className="text-sm text-slate-600 w-full border-none p-0 focus:ring-0 resize-none placeholder:text-slate-400 bg-transparent"
                        rows={2}
                        placeholder="Describe instructions or requirements..."
                        value={item.description}
                        onChange={(e) => updateItem(item._tempId, 'description', e.target.value)}
                      />
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Footer with General Deadline */}
            <div className="p-4 border-t border-slate-100 bg-white flex flex-col gap-4">
              
              {/* General Settings */}
              {requestQueue.length > 0 && (
                <div className="flex items-center gap-3 bg-slate-50 p-3 rounded-lg border border-slate-100">
                  <Clock className="h-4 w-4 text-slate-400" />
                  <span className="text-xs font-semibold text-slate-600 whitespace-nowrap">Due Date:</span>
                  <input 
                    type="date"
                    className="flex-1 bg-transparent border-none text-sm font-medium text-slate-700 focus:ring-0 p-0"
                    value={batchDeadline}
                    onChange={(e) => setBatchDeadline(e.target.value)}
                  />
                </div>
              )}

              <div className="flex justify-between items-center">
                 <div className="text-xs text-slate-400">
                   {requestQueue.length > 0 && "Review items before sending"}
                 </div>
                 <div className="flex gap-3">
                   <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 rounded-lg transition-colors">
                     Cancel
                   </button>
                   <button 
                     onClick={handleSendAll}
                     disabled={requestQueue.length === 0}
                     className="px-6 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-lg hover:shadow-xl transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transform active:scale-95"
                   >
                     <Send className="h-4 w-4" />
                     Send {requestQueue.length > 0 ? `${requestQueue.length} Requests` : 'Requests'}
                   </button>
                 </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RequestDocModal;